/**
 * "Restart to update" (desktop). The shell downloads and verifies a newer
 * version at launch and then waits: nothing installs until the user asks,
 * so an update never cuts a voice call by itself (docs/build/updater.md).
 * Nothing showed it before, so a ready update was downloaded again at every
 * launch and never installed (QA DT-BUG-02).
 *
 * As in Discord, it is a small button in the title bar (it covers nothing),
 * and Settings > About says what the updater is doing.
 *
 * "Update required" (Console v2 §3.7): a desktop app older than
 * `client.min_desktop_version` gets a screen in place of the app (the
 * gateway closes it with 4101 too), with "Restart to update" once the
 * update is downloaded, else the installer's download link.
 */
import { compareAppVersions } from '@neosocial/protocol';
import { useEffect, useState } from 'react';
import markUrl from '../assets/mark.svg';
import { useT } from '../i18n';
import { desktopBridge, serverOrigin } from '../lib/config';
import { installDesktopUpdate, useDesktopUpdate } from '../lib/desktopGlue';
import { useConfig } from '../store/config';
import { signOut } from '../store/session';
import { Btn, Icon } from '../ui/kit';
import { toast } from '../ui/overlay';
import { useVoice } from '../voice/store';

/**
 * The oldest version allowed when this desktop app is older (it must
 * update before it may connect), else null. The web app is never blocked.
 */
export function useUpdateRequired(): string | null {
  const min = useConfig((s) => s.config?.settings['client.min_desktop_version'] ?? '');
  const version = desktopBridge()?.appVersion;
  if (!min || !version) return null;
  return compareAppVersions(version, min) < 0 ? min : null;
}

/** The screen in place of the app while this desktop version is too old. */
export function UpdateRequiredScreen({ min, signedIn }: { min: string; signedIn: boolean }) {
  const { t } = useT();
  const version = desktopBridge()?.appVersion ?? '';
  const ready = useReadyUpdate();
  const status = useDesktopUpdate((s) => s.status);
  const installing = useDesktopUpdate((s) => s.installing);
  const [leaving, setLeaving] = useState(false);
  const line = ready
    ? t('ns.upd.ready', { version: ready })
    : status?.state === 'downloading'
      ? t('ns.upd.downloading', { version: status.version })
      : status?.state === 'checking'
        ? t('ns.upd.checking')
        : t('upd.req.manual');
  return (
    <div className="ns-splash ns-maint" role="main">
      <div className="nx-sky" aria-hidden="true">
        <i className="aur aur-hub" />
        <i className="grain" />
      </div>
      <div className="ns-maint-card nx-raised">
        <img src={markUrl} alt="" width={48} height={48} className="ns-splash-mark" />
        <Icon name="download" />
        <h1>{t('upd.req.title')}</h1>
        <p>{t('upd.req.body', { version, min })}</p>
        <p className="ns-maint-when" role="status">
          {line}
        </p>
        <div className="ns-row-btns ns-maint-acts">
          {ready ? (
            <Btn
              kind="primary"
              size="sm"
              busy={installing}
              onClick={() => void install(t('ns.upd.failed'))}
            >
              {installing ? t('ns.upd.installing') : t('ns.upd.restart')}
            </Btn>
          ) : (
            <a
              className="nx-btn primary sm"
              href={`${serverOrigin()}/download/Neosocial-Setup.exe`}
              target="_blank"
              rel="noreferrer"
            >
              <Icon name="download" />
              <span className="lbl">{t('upd.req.download')}</span>
            </a>
          )}
          {signedIn ? (
            <Btn
              kind="ghost"
              size="sm"
              busy={leaving}
              onClick={() => {
                setLeaving(true);
                void signOut().finally(() => setLeaving(false));
              }}
            >
              {t('ns.signout')}
            </Btn>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** The version waiting to be installed, or null. */
export function useReadyUpdate(): string | null {
  return useDesktopUpdate((s) => (s.status?.state === 'ready' ? s.status.version : null));
}

async function install(failedText: string): Promise<void> {
  await installDesktopUpdate();
  if (useDesktopUpdate.getState().failed) toast(failedText, { icon: 'alert', tone: 'warn' });
}

/** The versions this page already announced: one toast per version, not per render. */
const toasted = new Set<string>();

/**
 * A glass toast the moment an update is ready ("Update ready", Restart),
 * once per version; the title bar's chip stays as the lasting control. In a
 * voice call it says that restarting ends the call.
 */
export function UpdateReadyToast() {
  const { t } = useT();
  const version = useReadyUpdate();
  useEffect(() => {
    if (!version || toasted.has(version)) return;
    toasted.add(version);
    const v = useVoice.getState().session.status;
    const inVoice = v !== 'idle' && v !== 'failed';
    toast(t('ns.upd.toast', { version }) + (inVoice ? ` ${t('ns.upd.voiceNote')}` : ''), {
      icon: 'download',
      tone: 'ok',
      duration: 15_000,
      action: { label: t('ns.upd.toastRestart'), run: () => void install(t('ns.upd.failed')) },
    });
  }, [version, t]);
  return null;
}

/** The title bar's button while an update is ready. */
export function UpdateChip() {
  const { t } = useT();
  const version = useReadyUpdate();
  const installing = useDesktopUpdate((s) => s.installing);
  const inVoice = useVoice((v) => v.session.status !== 'idle' && v.session.status !== 'failed');
  if (!version) return null;
  const tip = t('ns.upd.ready', { version }) + (inVoice ? ` ${t('ns.upd.voiceNote')}` : '');
  return (
    <button
      type="button"
      className="nx-chip ns-upd-chip"
      data-tone="ok"
      dir="ltr"
      data-tip={tip}
      aria-label={`${t('ns.upd.restart')}. ${tip}`}
      disabled={installing}
      onClick={() => void install(t('ns.upd.failed'))}
    >
      {installing ? <i className="nx-spin" /> : <Icon name="download" />}
      {installing ? t('ns.upd.installing') : t('ns.upd.restart')}
    </button>
  );
}

/** Settings > About: what the updater is doing, and the restart button when ready. */
export function UpdateStatusLine() {
  const { t } = useT();
  const status = useDesktopUpdate((s) => s.status);
  const installing = useDesktopUpdate((s) => s.installing);
  const failed = useDesktopUpdate((s) => s.failed);
  if (!status) return null;
  switch (status.state) {
    case 'ready':
      return (
        <div className="ns-upd-about">
          <p>{t('ns.upd.ready', { version: status.version })}</p>
          {failed ? (
            <span className="ns-upd-err" role="alert">
              {t('ns.upd.failed')}
            </span>
          ) : null}
          <Btn
            kind="primary"
            size="sm"
            busy={installing}
            onClick={() => void installDesktopUpdate()}
          >
            {installing ? t('ns.upd.installing') : t('ns.upd.restart')}
          </Btn>
        </div>
      );
    case 'checking':
      return <p className="ns-muted">{t('ns.upd.checking')}</p>;
    case 'downloading':
      return <p className="ns-muted">{t('ns.upd.downloading', { version: status.version })}</p>;
    case 'installing':
      return <p className="ns-muted">{t('ns.upd.installing')}</p>;
    case 'upToDate':
      return <p className="ns-muted">{t('ns.upd.upToDate')}</p>;
    case 'error':
      // what failed: an install that failed says so, as its toast does (QA 2026-10-28:
      // it said "Couldn't check for updates" after the check and download had worked);
      // a shell from before `stage` is known by this page's own failed install
      return (
        <p className="ns-muted" role="status">
          {t(
            status.stage === 'install' || (!status.stage && failed)
              ? 'ns.upd.failed'
              : status.stage === 'download'
                ? 'ns.upd.downloadFailed'
                : 'ns.upd.error',
          )}
        </p>
      );
    default:
      // idle, or off in this build (development, or no update server yet): nothing to say
      return null;
  }
}
